<script setup lang="ts">
import { ref } from 'vue';
import { default as ButtonEl } from '@/components/shared/Button.vue';
import Keyboard from '@/components/shared/Keyboard.vue';
import { useMeetingsStore } from '@/stores/meetings';
import BaseOverlay from '@/overlays/BaseOverlay.vue';
import AuthFlow from '@/components/shared/AuthFlow.vue';
import { Profile_Type } from '@/graphql/generated-types';
import { eventBus } from '@/utils/eventBus';
import { useI18n } from 'vue-i18n';
import { ProfileFragment } from '@/graphql/custom-types';

const meetingsStore = useMeetingsStore();
const emit = defineEmits(['close']);

interface Props {
  meeting: any;
}

const props = defineProps<Props>();

const suggestion = ref<string>('');
const showAuthFlow = ref<boolean>(false);
const authFlowLoading = ref<boolean>(false);

const { t } = useI18n();

async function addSuggestion(profile: ProfileFragment, pin: string) {
  try {
    authFlowLoading.value = true;

    await meetingsStore.meetingSuggest({
      profile: {
        pin,
        profileId: profile.id,
        profileType: profile.type as Profile_Type,
      },
      meetingId: props.meeting.id,
      suggestion: suggestion.value,
    });

    eventBus.emit('success', t('meeting.suggestionSaved'));
  } catch {
    eventBus.emit('anErrorOccurred');
  } finally {
    authFlowLoading.value = false;
    showAuthFlow.value = false;
  }
}
</script>

<template>
  <BaseOverlay
    @close="emit('close')"
    :title="$t('meeting.writeSuggestion')"
    :scrollHeight="500"
  >
    <template #content>
      <div class="flex-2">
        <Keyboard
          placeholder="meeting.suggestion"
          @onChange="suggestion = $event"
        />
      </div>
    </template>

    <template #actions>
      <div class="flex w-full justify-center bottom-0">
        <ButtonEl
          text="meeting.writeSuggestion"
          :icon="['far', 'fa-edit']"
          background="bg-darkOcean-600"
          textColor="text-neutral-0"
          :disabled="!suggestion.length"
          rounded="full"
          padding="px-16"
          typography="h700"
          @click="showAuthFlow = true"
        />
      </div>
    </template>
  </BaseOverlay>

  <AuthFlow
    v-if="showAuthFlow"
    :title="$t('meetings.addSuggestionPinTitle')"
    :loading="authFlowLoading"
    @close="showAuthFlow = false"
    @authenticated="(profile, pin) => addSuggestion(profile, pin)"
  />
</template>
